/**
 * (c) 2021, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */

import { fromByteArray } from "base64-js";
import { MAIN_FILE } from "./fs";

/**
 * We can now initialize a project with multiple files.
 * Handling is in place for backwards compatibility for V2 projects
 * where only the main file content is initialized as a string.
 */
export interface PythonProject {
  // File content as base64.
  files: Record<string, string>;
  projectName?: string;
}

/**
 *
 * @param project PythonProject.
 * @returns PythonProject where all file content has been converted to base64.
 */
export const projectFilesToBase64 = (
  files: Record<string, string>
): Record<string, string> => {
  for (const file in files) {
    files[file] = fromByteArray(new TextEncoder().encode(files[file]));
  }
  return files;
};

/**
 * Kommentare des Startprogramms je Sprache.
 *
 * Uebersetzt werden nur die Kommentare; der Code und die Ausgabe bleiben in
 * allen Sprachen gleich, wie in den Lernkapiteln. Die Namen der Knoepfe und
 * Reiter entsprechen den Beschriftungen der Oberflaeche in dieser Sprache.
 */
interface StarterComments {
  intro: string[];
  led: string;
}

const starterComments: Record<string, StarterComments> = {
  de: {
    intro: [
      "Willkommen im Editor von Mechatronics @ BME!",
      "",
      "So benutzt du den Editor:",
      "- Schreib dein Programm hier. Alles nach # ist ein Kommentar.",
      '- "An Roboter senden" (unten links) verbindet sich per Kabel',
      "  oder Bluetooth mit dem Roboter, uebertraegt dieses Programm",
      "  und startet es.",
      '- "Stopp" beendet das laufende Programm.',
      "- Ausgaben von print() erscheinen unten im seriellen Terminal.",
      "- Referenz: Kapitel von den Python-Grundlagen bis zur Regelung.",
      "- Uebungen: Versuche am Roboter.",
      "- API: alle Befehle und Einstellungen des Roboters.",
      '- "Speichern" laedt dein Programm herunter, "Oeffnen" laedt',
      "  es wieder.",
      "- Die Sprache stellst du ueber das Zahnrad unten links ein:",
      "  Einstellungen > Sprache (Deutsch, English, Tieng Viet).",
    ],
    led: "Die Status-LED haengt an GP11.",
  },
  en: {
    intro: [
      "Welcome to the Mechatronics @ BME editor!",
      "",
      "How to use the editor:",
      "- Write your program here. Everything after # is a comment.",
      '- "Send to robot" (bottom left) connects to the robot by',
      "  cable or Bluetooth, transfers this program and starts it.",
      '- "Stop" ends the running program.',
      "- print() output appears in the serial terminal below.",
      "- Reference: chapters from Python basics to closed-loop control.",
      "- Exercises: experiments on the robot.",
      "- API: every robot command and all robot settings.",
      '- "Save" downloads your program, "Open…" loads it again.',
      "- Change the language with the gear icon at the bottom left:",
      "  Settings > Language (Deutsch, English, Tiếng Việt).",
    ],
    led: "The status LED is connected to GP11.",
  },
  vi: {
    intro: [
      "Chào mừng bạn đến với trình soạn thảo Mechatronics @ BME!",
      "",
      "Cách dùng trình soạn thảo:",
      "- Viết chương trình của bạn ở đây. Mọi thứ sau # là chú thích.",
      '- "Gửi tới robot" (dưới cùng bên trái) kết nối với robot qua',
      "  cáp hoặc Bluetooth, truyền chương trình này và chạy nó.",
      '- "Dừng" kết thúc chương trình đang chạy.',
      "- Kết quả của print() hiện ra trong cửa sổ cổng nối tiếp bên dưới.",
      "- Tham khảo: các chương từ Python cơ bản đến điều khiển vòng kín.",
      "- Bài tập: thí nghiệm trên robot.",
      "- API: mọi lệnh và thiết lập của robot.",
      '- "Lưu" tải chương trình của bạn xuống, "Mở…" nạp lại nó.',
      "- Đổi ngôn ngữ bằng biểu tượng bánh răng ở dưới cùng bên trái:",
      "  Cài đặt > Ngôn ngữ (Deutsch, English, Tiếng Việt).",
    ],
    led: "Đèn LED trạng thái nối với GP11.",
  },
};

/**
 * Startprogramm fuer eine Sprachkennung; Sprachen ohne eigene Kommentare
 * bekommen die deutschen, wie die Oberflaeche beim ersten Start.
 */
export const mainFileContentFor = (languageId: string): string => {
  const { intro, led } =
    starterComments[languageId.toLowerCase().split("-")[0]] ??
    starterComments.de;
  const kopf = intro.map((zeile) => (zeile ? `# ${zeile}` : "#")).join("\n");
  return `${kopf}

import robot
from machine import Pin

# ${led}
led = Pin(11, Pin.OUT)

while True:
    led.toggle()
    print("Hello world!")
    robot.sleep(1)
`;
};

export const defaultMainFileContent = mainFileContentFor("de");

export const initialProjectFor = (languageId: string): PythonProject => ({
  files: projectFilesToBase64({
    [MAIN_FILE]: mainFileContentFor(languageId),
  }),
});

export const defaultInitialProject: PythonProject = {
  files: projectFilesToBase64({
    [MAIN_FILE]: defaultMainFileContent,
  }),
};
